import React from "react";
import { PROJECTS } from "../lib/site";
import { GripVertical } from "lucide-react";

function BeforeAfterCard({ project }) {
  const [pos, setPos] = React.useState(50);
  const ref = React.useRef(null);
  const dragging = React.useRef(false);

  const updateFromClientX = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  const onDown = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);
  };
  const onUp = () => { dragging.current = false; };

  React.useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <div
      ref={ref}
      onMouseDown={onDown}
      onTouchStart={onDown}
      className="relative aspect-[4/3] rounded-2xl overflow-hidden card-tactile cursor-ew-resize select-none"
      data-testid={`ba-card-${project.title}`}
    >
      <img src={project.after} alt={`${project.title} - dopo`} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={project.before}
          alt={`${project.title} - prima`}
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${(100 / Math.max(pos, 0.001)) * 100}%`, maxWidth: "none" }}
        />
      </div>

      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-brand-ink/80 text-white backdrop-blur">
        Prima
      </div>
      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold bg-brand-terracotta text-white backdrop-blur">
        Dopo
      </div>

      <div className="absolute top-0 bottom-0 w-[3px] bg-white/90 shadow-[0_0_20px_rgba(0,0,0,0.25)]" style={{ left: `${pos}%`, transform: "translateX(-50%)" }} />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white border border-brand-line shadow-lg flex items-center justify-center"
        style={{ left: `${pos}%` }}
      >
        <GripVertical className="h-5 w-5 text-brand-ink" />
      </div>

      <div className="absolute bottom-3 left-3 right-3 bg-white/85 backdrop-blur px-3 py-2 rounded-xl text-sm font-medium">
        {project.title}
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section id="progetti" className="py-20 md:py-28" data-testid="ba-section">
      <div className="container-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Prima · Dopo</span>
            <h2 className="heading-display text-3xl md:text-5xl mt-4">
              Trascina lo slider e scopri la differenza.
            </h2>
            <p className="text-brand-mute mt-5 leading-relaxed">
              Tre cantieri reali firmati Edil Murgia. Tieni premuto il pulsante e
              scorri per confrontare lo stato originario con il risultato finale.
            </p>
          </div>
          <div className="text-sm text-brand-mute italic">
            Trascina la maniglia centrale per confrontare prima e dopo.
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECTS.map((p) => (
            <BeforeAfterCard key={p.title} project={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
